// PatientDataContext.js
import React, { createContext, useState, useCallback, useRef, useLayoutEffect } from 'react';
import { auth, db } from '../firebase';
import { doc, getDoc, runTransaction } from 'firebase/firestore';
import { deepMerge } from '../utils/deepMerge';
import debounce from 'lodash.debounce';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';

export const PatientDataContext = createContext();

// How long the authenticated clinician's own document is cached for within a
// session before it is re-read from Firestore. Bounds how long a mid-session
// assignment change can be missed; the retry-once safeguard in fetchPatientData
// closes that gap immediately when access appears denied.
const CLINICIAN_DOC_TTL_MS = 5 * 60 * 1000; // 5 minutes

export const PatientDataProvider = ({ children }) => {

  // Initialise the state patientDataModel with data from localStorage if available, ensuring persistence across page reloads.
  // Ensures that any previously saved data is loaded into the state when the app starts.
  const [patientDataModel, setPatientDataModel] = useState(() => {
    const storedData = localStorage.getItem('patientDataModel');
    return storedData ? JSON.parse(storedData) : null;
  });

  // Tracks the patient ID that was last successfully fetched and loaded
  // into patientDataModel. Used in the catch block of fetchPatientData to
  // distinguish "re-fetch of the same patient failed transiently" (preserve
  // existing data so the clinician doesn't lose work) from "fetch of a
  // different patient failed" (clear data and surface error to avoid
  // cross-patient data display). A ref is used instead of state because:
  //   1. It doesn't need to trigger re-renders
  //   2. It's always current inside useCallback closures regardless of
  //      the dependency array (avoids the stale closure issue)
  const loadedPatientIdRef = useRef(null);

  // Render-gating companion to loadedPatientIdRef.
  //
  // PatientDataProvider is mounted once at the app root and never unmounts, so
  // patientDataModel persists across patient switches. Components that display
  // patient data (e.g. PatientInfoPanel) must NOT render the previous patient's
  // data while the new patient is still being fetched. PatientRouteWrapper gates
  // rendering on `loadedPatientId === patientId` to guarantee this.
  //
  // Why a separate piece of state rather than reusing loadedPatientIdRef?
  //   - The ref is read synchronously inside async closures (the fetch catch
  //     block) and must always be current there — but a ref change does NOT
  //     trigger a re-render, so the wrapper wouldn't update when data arrives.
  //   - This state DOES trigger the re-render the wrapper needs. The two are
  //     always written together so they never diverge.
  //
  // Seeded from a localStorage companion key so a cold reload of the SAME
  // patient paints instantly (parity with the patientDataModel localStorage
  // hydration above). A cold load of a DIFFERENT patient won't match, so the
  // wrapper correctly shows a loading state instead of stale data.
  const [loadedPatientId, setLoadedPatientId] = useState(() => {
    return localStorage.getItem('loadedPatientId') || null;
  });

  // Session cache for the authenticated clinician's own document.
  //
  // Every patient switch calls fetchPatientData, which previously did TWO
  // sequential Firestore reads: the clinician's own doc (for an access check)
  // then the patient doc. The clinician doc (organisation, userType, assigned
  // patients) rarely changes mid-session, so re-reading it on every switch is
  // wasteful and roughly doubles the time before the new patient appears.
  //
  // We cache it for CLINICIAN_DOC_TTL_MS. The TTL bounds how long a mid-session
  // change (e.g. a new patient assignment) can be missed; the retry-once
  // safeguard in fetchPatientData closes that gap on the spot when access
  // appears denied. The cache stores the uid it belongs to so a sign-out →
  // sign-in on the same browser can never serve one user's doc to another.
  // Firestore security rules remain the authoritative access gate regardless.
  const clinicianDocCache = useRef({ data: null, uid: null, fetchedAt: 0 });

  // Fetch the clinician's document from Firestore to verify the authenticated user and access their data.
  // Pass { forceRefresh: true } to bypass the session cache (used by the
  // retry-once safeguard when a cached doc appears to deny access).
  const getUserClinicianDoc = useCallback(async ({ forceRefresh = false } = {}) => {
    const user = auth.currentUser;
    if (!user) {
      console.warn("No authenticated user found.");
      return null;
    }

    // Serve from the session cache when it is fresh AND belongs to the
    // current user. This is the read we are eliminating on patient switches.
    const cache = clinicianDocCache.current;
    if (
      !forceRefresh &&
      cache.data &&
      cache.uid === user.uid &&
      (Date.now() - cache.fetchedAt) < CLINICIAN_DOC_TTL_MS
    ) {
      return cache.data;
    }

    const clinicianDocRef = doc(db, 'clinicians', user.uid);
    const clinicianDoc = await getDoc(clinicianDocRef);
  
    if (!clinicianDoc.exists()) {
      console.warn(`Clinician not found with user ID: ${user.uid}`);
      return null;
    }

    const data = clinicianDoc.data();
    clinicianDocCache.current = { data, uid: user.uid, fetchedAt: Date.now() };
    return data;
  }, []);
  
    // Clear the patient data from both the local state and localStorage.
  // Useful for resetting the state when needed, such as when logging out or switching patients.
  const clearPatientData = useCallback(() => {
    setPatientDataModel(null);
    loadedPatientIdRef.current = null;
    // Reset the render-gating state + its companion key so the wrapper shows a
    // loading state (never stale data) the next time a patient page mounts.
    setLoadedPatientId(null);
    localStorage.removeItem('loadedPatientId');
    localStorage.removeItem('patientDataModel');
    // Drop the cached clinician doc. clearPatientData runs on sign-out, so this
    // prevents one user's cached doc surviving into another user's session.
    clinicianDocCache.current = { data: null, uid: null, fetchedAt: 0 };
    setError(null);
    // Discard any pending writes from the previous patient to prevent
    // cross-patient data contamination.  Without this, stale queue
    // entries could be flushed into a different patient's document
    // when the next updatePatientFirestore call triggers the debounce.
    updateQueue.current = [];
    transientRetryCount.current = 0;
    isProcessing.current = false;
    pendingDrainPatientId.current = null;
    resolveDrainCallbacks();
  }, []);
  
  // Add error state to track and display errors
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  // Helper function to check if clinician has access to patient
  const checkClinicianAccess = (clinicianData, patientId, patientData) => {
    // Allow org admin and support users to access all patients in their organization
    if (clinicianData.userType === 'Org. Admin' || clinicianData.userType === 'Support') {
      // Check if patient belongs to the same organization as the clinician      
      return patientData && patientData.organisation === clinicianData.organisation;
    }
    // For regular clinicians, check if they're specifically assigned
    else if (!clinicianData.patients || !clinicianData.patients[patientId]) {
      return false;
    }
    return true;
  };

  // Fetch patient data from Firestore and update both the local state and localStorage
  const fetchPatientData = useCallback(async (patientId) => {
    try {
      setError(null); // Reset error state

      const clinicianData = await getUserClinicianDoc();
      if (!clinicianData) {
        throw new Error('Not authorized to access patient data');
      }
      
      const patientDocRef = doc(db, 'patients', patientId);
      const patientDoc = await getDoc(patientDocRef);
      
      if (!patientDoc.exists()) {
        clearPatientData();
        navigate('/dashboard', { 
          state: { 
            error: `Patient ${patientId} not found` 
          }
        });
        throw new Error(`Patient ${patientId} not found`);
      }
            
      const patientData = patientDoc.data();
 
      // Check access using organization for admins/support or specific assignment for clinicians
      let hasAccess = checkClinicianAccess(clinicianData, patientId, patientData);

      // Retry-once safeguard for the cached clinician doc.
      //
      // The clinicianData above may have been served from the session cache. If
      // it says "no access", the cache could simply be stale — e.g. the
      // clinician was just assigned to this patient and the cached copy predates
      // that. Before redirecting them away, force a fresh read of the clinician
      // doc and re-check. This makes the cache fully transparent: it never
      // produces a false "access denied" that a fresh read wouldn't.
      if (!hasAccess) {
        const freshClinicianData = await getUserClinicianDoc({ forceRefresh: true });
        if (freshClinicianData) {
          hasAccess = checkClinicianAccess(freshClinicianData, patientId, patientData);
        }
      }

      if (!hasAccess) {
        // Clear any existing patient data to prevent data mixup
        clearPatientData();
        // Redirect to dashboard with error
        navigate('/dashboard', { 
          state: { 
            error: `You don't have access to patient ${patientId}` 
          }
        });
        throw new Error(`Not authorized to access patient ${patientId}`);
      }
      
      setPatientDataModel(patientData);
      loadedPatientIdRef.current = patientId;
      localStorage.setItem('patientDataModel', JSON.stringify(patientData));
      // Surface the loaded patient id as render-gating state AFTER the model is
      // set. Ordering matters: if this state flipped to the new id BEFORE the
      // model updated, an intermediate render could show the previous patient's
      // data under a matching id. Setting the model first guarantees the only
      // possible intermediate render is "new data, id not yet matched" (loading),
      // which is safe.
      setLoadedPatientId(patientId);
      localStorage.setItem('loadedPatientId', patientId);
  
    } catch (error) {
      console.error("Error fetching patient data: ", error);
      if (loadedPatientIdRef.current === patientId) {
        // Same patient re-fetch failed transiently — preserve existing
        // data so the clinician doesn't lose work in progress.
        console.warn("[PatientDataContext] Transient fetch error for current patient, preserving data");
      } else {
        // Different patient or no prior data — clear and surface error
        setError(error.message);
        clearPatientData();
      }
    }
  }, [getUserClinicianDoc, navigate, clearPatientData]);

  // A ref to store a queue of updates that need to be applied to the patient's data model.
  // This allows us to accumulate multiple updates and process them together, reducing the number of writes to Firestore.
  const updateQueue = useRef([]);

  // Tracks consecutive transient failures (e.g. transaction contention).
  // We only surface a toast to the user after MAX_TRANSIENT_RETRIES are
  // exhausted, so that momentary contention (which self-resolves on the
  // next attempt) doesn't produce unnecessary alarm.  Reset to 0 on
  // every successful write.
  const transientRetryCount = useRef(0);
  const MAX_TRANSIENT_RETRIES = 3;

  // Mutex: prevents concurrent runTransaction calls.  Without this,
  // the 200ms debounce can fire a second transaction while the first
  // is still in its internal 5-retry cycle, creating concurrent
  // transactions that contend with *each other* on top of any
  // Cloud Function writes — producing the cascading 400 storm seen
  // in the console when the clinician navigates rapidly.
  const isProcessing = useRef(false);

  // Stores the patientId for a deferred drain.  When the debounced
  // function fires while a transaction is already in-flight, we can't
  // process the queue immediately — but we remember the patientId so
  // we can drain the queue once the current transaction finishes.
  const pendingDrainPatientId = useRef(null);

  // Callbacks registered by flushPendingUpdates() that should be resolved
  // once ALL queued writes (including in-flight transactions and subsequent
  // drain cycles) have been committed to Firestore.  This replaces the old
  // approach where flushPendingUpdates returned Promise.resolve() immediately
  // during isProcessing contention, which gave callers a false "all done" signal.
  const drainCallbacks = useRef([]);

  // Resolves and clears all pending drain callbacks.  Called from the
  // finally block when the queue is fully drained (no more items, no
  // pending drain), and from sign-out / clearPatientData to prevent
  // Promises from hanging indefinitely.
  const resolveDrainCallbacks = () => {
    if (drainCallbacks.current.length === 0) return;
    const cbs = [...drainCallbacks.current];
    drainCallbacks.current = [];
    cbs.forEach(cb => cb());
  };

  // A debounced function that processes the update queue and applies all accumulated updates to Firestore.
  // The debouncing ensures that updates are batched and sent to Firestore at most once every 200 milliseconds.
  // This reduces the load on Firestore and improves performance by minimizing the number of write operations.
  // By storing the debounced function in a useRef, we make sure the same function instance is used throughout the component's lifecycle.
  const debouncedProcessQueue = useRef(debounce(async (patientId) => {
    if (updateQueue.current.length === 0) {
      return Promise.resolve();
    }

    // If another transaction is already running, don't start a second
    // one.  The queued items stay in updateQueue and will be picked up
    // when the in-flight transaction completes (see the drain-after-
    // processing block at the bottom of this function).
    if (isProcessing.current) {
      pendingDrainPatientId.current = patientId;
      console.debug('[PatientDataContext] Transaction already in-flight — deferring queue drain');
      return Promise.resolve();
    }

    // If the user has already signed out (or the auth session expired
    // while the tab was backgrounded), silently discard the queue rather
    // than draining it into the try/catch and surfacing an error toast.
    //
    // We also clear the queue here to prevent stale writes: because
    // PatientDataProvider is mounted at the app root and never unmounts,
    // leftover queue items could survive a sign-out → sign-in cycle and
    // be flushed into a different patient's document on the next trigger.
    // Since auth.currentUser is only null on explicit sign-out (not during
    // background token refreshes), these writes are genuinely unsalvageable.
    const user = auth.currentUser;
    if (!user) {
      const discarded = updateQueue.current.length;
      updateQueue.current = [];
      resolveDrainCallbacks();
      console.debug(`[PatientDataContext] No authenticated user — discarded ${discarded} queued update(s)`);
      return Promise.resolve();
    }

    // Copy the current queue and reset it immediately to prevent race
    // conditions.  Declared outside try so the catch block can push
    // failed updates back onto the queue for retry.
    const currentUpdates = [...updateQueue.current];
    updateQueue.current = [];

    isProcessing.current = true;
    // Set to false in the contention handler so the finally block doesn't
    // immediately re-trigger a drain — we want to wait for the next
    // natural trigger (user action / navigation flush) to give Cloud
    // Function writes time to settle.
    let drainAfterProcessing = true;

    try {
      
      // Run the entire update as a transaction
      await runTransaction(db, async (transaction) => {
        // Get patient document reference
        const patientDocRef = doc(db, 'patients', patientId);
        
        // Read current patient data inside transaction
        const patientDoc = await transaction.get(patientDocRef);
        
        if (!patientDoc.exists()) {
          throw new Error(`Patient document not found for ID: ${patientId}`);
        }
        
        const patientData = patientDoc.data();
        
        // Check if clinician is assigned to any stage for this patient.
        // Guard against null/undefined entries — some clinician role slots may
        // be unassigned (null) in Firestore when no clinician has been allocated.
        const hasAccess = Object.values(patientData.clinicians || {}).some(
          clinicianRole => 
            clinicianRole &&
            (clinicianRole.clinicianId === user.uid || 
            (clinicianRole.supportingClinicianIds && 
            clinicianRole.supportingClinicianIds.includes(user.uid)))
        );
        
        if (!hasAccess) {
          throw new Error(`Not authorized to update patient ${patientId}`);
        }
        
        // Initialize dataModel if it doesn't exist
        if (!patientData.dataModel) {
          patientData.dataModel = {};
        }
        
        // Create an efficient update object that only includes changed paths
        let finalUpdate = {};
        
        // Process each update by identifying the specific paths being updated
        currentUpdates.forEach(update => {
          // Identify the top-level keys in the update (like '2_clinicalInterview')
          Object.keys(update).forEach(topLevelKey => {
            // If this is the first update for this top-level key, initialize from patient data
            if (!finalUpdate[topLevelKey]) {
              finalUpdate[topLevelKey] = patientData.dataModel[topLevelKey] || {};
            }
            
            // Now perform a targeted merge only on this section
            finalUpdate[topLevelKey] = deepMerge(finalUpdate[topLevelKey], update[topLevelKey]);
          });
        });
        
        // Only update the parts of dataModel that have changed
        const updateObject = {};
        Object.keys(finalUpdate).forEach(key => {
          updateObject[`dataModel.${key}`] = finalUpdate[key];
        });
        
        // Write only the changed paths back to Firestore
        transaction.update(patientDocRef, updateObject);
      });
      
      transientRetryCount.current = 0;
      console.log(`Updated patient data model for patientId: ${patientId} with ${currentUpdates.length} updates`);

      // Refresh local state from Firestore so the UI reflects any
      // server-side changes (e.g. Cloud Function writes that landed
      // between our read and commit).  This is wrapped in its own
      // try/catch because the transaction has already committed — a
      // failure here means the data IS saved, we just can't refresh
      // the local view.  Showing an error toast would be misleading.
      try {
        const patientDocRef = doc(db, 'patients', patientId);
        const updatedDoc = await getDoc(patientDocRef);
        const updatedPatient = updatedDoc.data();
        setPatientDataModel(updatedPatient);
        localStorage.setItem('patientDataModel', JSON.stringify(updatedPatient));
      } catch (refreshError) {
        console.warn(
          '[PatientDataContext] Transaction committed but local state refresh failed — ' +
          'data is saved, UI may be stale until next navigation.',
          refreshError
        );
      }

      return Promise.resolve();
    } catch (error) {
      console.error("Error updating patient data model: ", error);

      // If the user signed out while the Firestore transaction was
      // in-flight, the server rejects it with "permission-denied" even
      // though auth.currentUser was non-null when we started.  In this
      // case, silently discard — the user is leaving and the writes are
      // unsalvageable.  Showing an error toast here would be confusing.
      if (!auth.currentUser) {
        const discardedCount = updateQueue.current.length + currentUpdates.length;
        updateQueue.current = [];
        resolveDrainCallbacks();
        console.debug('[PatientDataContext] User signed out during flush — discarded failed writes');
        return;
      }

      // Classify the error to decide whether a silent retry is appropriate.
      // Permanent errors (auth, authorization, missing docs) will never
      // succeed on retry, so we surface them immediately.  Transient errors
      // (transaction contention, network hiccup) are silently re-queued up
      // to MAX_TRANSIENT_RETRIES times before we alert the user.
      const msg = error.message || '';
      const code = error.code || '';
      const isPermanent =
        msg.includes('No authenticated user') ||
        msg.includes('Not authorized') ||
        msg.includes('not found') ||
        msg.includes('insufficient permissions') ||
        code === 'permission-denied';

      // Transaction contention (failed-precondition / version mismatch).
      // runTransaction already retried 5 times internally before throwing,
      // so immediate custom retry would just start another 5-retry cycle
      // that contends with Cloud Function writes. Instead, push the updates
      // back to the queue silently — the next natural trigger (user action,
      // navigation flush) will attempt when contention has likely subsided.
      const isContention =
        code === 'failed-precondition' ||
        msg.includes('does not match the required base version');

      if (isContention) {
        transientRetryCount.current += 1;
        // Tell the finally block not to immediately drain — we want
        // to wait for the next natural trigger so Cloud Functions
        // have time to finish writing and the version stabilises.
        drainAfterProcessing = false;

        if (transientRetryCount.current <= MAX_TRANSIENT_RETRIES) {
          console.debug(
            `[PatientDataContext] Transaction contention (attempt ${transientRetryCount.current}/${MAX_TRANSIENT_RETRIES}), ` +
            `re-queuing ${currentUpdates.length} update(s) — will retry on next trigger, not immediately`
          );
          updateQueue.current.unshift(...currentUpdates);
          return;
        }

        // Contention persisted across MAX_TRANSIENT_RETRIES natural
        // triggers. Surface a non-alarming toast but keep updates in
        // the queue — they'll be attempted on the next navigation or
        // user action.
        console.warn(
          `[PatientDataContext] Transaction contention persisted after ${MAX_TRANSIENT_RETRIES} retries`
        );
        transientRetryCount.current = 0;
        updateQueue.current.unshift(...currentUpdates);
        toast.error(
          'Some changes are still saving. They will be saved automatically — please continue working.',
          { toastId: 'patient-data-save-error' }
        );
        return;
      }

      if (!isPermanent) {
        transientRetryCount.current += 1;

        if (transientRetryCount.current <= MAX_TRANSIENT_RETRIES) {
          // Silent retry for non-contention transient errors (network
          // hiccups, etc.). Safe to retry immediately since these don't
          // cause cascading contention.
          console.debug(
            `[PatientDataContext] Transient error (attempt ${transientRetryCount.current}/${MAX_TRANSIENT_RETRIES}), ` +
            `re-queuing ${currentUpdates.length} update(s): ${code || msg}`
          );
          updateQueue.current.unshift(...currentUpdates);
          // Don't kick the debounce; the drain-after-processing block
          // below will pick up the queue items immediately.
          return;
        }

        // Retries exhausted — surface the failure and stop retrying to
        // avoid an infinite loop.
        console.warn(
          `[PatientDataContext] Transient error persisted after ${MAX_TRANSIENT_RETRIES} retries — giving up`
        );
        transientRetryCount.current = 0;
      }

      // Only surface errors to the user for permanent failures or when
      // transient retries are exhausted.  Uses a fixed toastId so that
      // concurrent failures collapse into a single toast.
      // Note: we intentionally do NOT call setError() for transient
      // errors — setError triggers a React state update that can cause
      // a re-render cascade / blank page during contention storms.
      toast.error(
        'Your latest changes may not have saved. Please check your connection and try again.',
        { toastId: 'patient-data-save-error' }
      );
    } finally {
      isProcessing.current = false;

      // If new items accumulated in the queue while this transaction was
      // running (debounce fired but hit the isProcessing guard above),
      // drain them now — UNLESS the contention handler suppressed it
      // (drainAfterProcessing = false) to avoid re-entering the
      // contention cycle immediately.
      //
      // setTimeout(0) yields to the event loop so the Firestore SDK's
      // internal state has time to settle.
      if (drainAfterProcessing && updateQueue.current.length > 0 && auth.currentUser) {
        const drainId = pendingDrainPatientId.current || patientId;
        pendingDrainPatientId.current = null;
        // Don't resolve drainCallbacks yet — the drain cycle will
        // process remaining items and resolve them when truly done.
        setTimeout(() => debouncedProcessQueue(drainId), 0);
      } else {
        pendingDrainPatientId.current = null;
        resolveDrainCallbacks();
      }
    }
  }, 200)).current; // Ensure the debounced function is created only once - so we can call the debounced function without re-creating it

  // Adds an update to the queue SYNCHRONOUSLY, then kicks the debounce
  // timer.  The previous implementation did two Firestore reads (clinician
  // doc + patient doc) before pushing to the queue — those reads were
  // redundant because debouncedProcessQueue already validates access
  // inside its transaction — and they made the queue push asynchronous,
  // which meant a flush() call immediately after could miss the update.
  //
  // Returns Promise.resolve() for backward compatibility: callers that
  // await this function (e.g. sendSectionUpdates) continue to resolve
  // instantly, preserving frictionless navigation.  The real Firestore
  // write happens when the 200ms debounce fires (or on flush).
  //
  // Security is enforced by three remaining layers:
  //   1. Frontend canEdit guards (stage-specific, every handler)
  //   2. debouncedProcessQueue transaction check (clinicians-map lookup)
  //   3. Firestore security rules (blocks dataModel writes from
  //      unassigned Org Admin / Support)
  const updatePatientFirestore = useCallback((patientId, updates) => {
    updateQueue.current.push(updates);
    debouncedProcessQueue(patientId);
    return Promise.resolve();
  }, [debouncedProcessQueue]);


  // ─── Part 2: Auto-flush on route change ───────────────────────────
  // When the URL changes (side nav, top nav, browser back/forward),
  // we blur the active element first — this fires any textarea's
  // onBlur handler, which queues a write — then immediately flush
  // the debounce queue so the write is initiated before React
  // unmounts the outgoing page's components.
  //
  // useLayoutEffect runs BEFORE the new page renders, so the blur +
  // flush complete synchronously before any new component mounts or
  // calls fetchPatientData (avoiding the CSD race condition where
  // a re-fetch would overwrite unsaved local state).
  const location = useLocation();

  useLayoutEffect(() => {
    return () => {
      if (document.activeElement && document.activeElement !== document.body) {
        document.activeElement.blur();
      }
      debouncedProcessQueue.flush();
    };
  }, [location.pathname, debouncedProcessQueue]);

  // ─── Part 3: Explicit flush for navigation handlers ──────────────
  // Returns a Promise that resolves when ALL queued writes — including
  // any transaction currently in-flight and any subsequent drain cycles —
  // have been committed to Firestore.  This is the "data ready" signal
  // that TriggerGenerateDocumentSection chains to, guaranteeing the
  // Cloud Function always reads fresh data.
  //
  // How it works:
  //   1. Blur the active element so any onBlur handler queues its write.
  //   2. Call flush() to kick processing immediately (cancels debounce).
  //   3. If the queue is empty and nothing is in-flight, return resolved.
  //   4. Otherwise register a callback that the finally block of
  //      debouncedProcessQueue will resolve once fully drained.
  //   5. A 5-second safety timeout prevents indefinite hanging on
  //      persistent failures — on timeout the Promise resolves anyway,
  //      which is still safer than the old arbitrary 4-second delay.
  const FLUSH_TIMEOUT_MS = 5000;

  const flushPendingUpdates = useCallback(({ noTimeout = false } = {}) => {
    if (document.activeElement && document.activeElement !== document.body) {
      document.activeElement.blur();
    }

    // Kick processing immediately (supersedes the 200ms debounce timer)
    debouncedProcessQueue.flush();

    // Fast path: nothing pending, nothing in-flight — already committed
    if (!isProcessing.current && updateQueue.current.length === 0) {
      return Promise.resolve();
    }

    // Slow path: writes are in-flight or queued.  Register a callback
    // that the finally block resolves once the queue is fully drained.
    return new Promise((resolve) => {
      drainCallbacks.current.push(resolve);

      // When noTimeout is true, the promise only resolves when the queue
      // is genuinely drained — used by generation-chaining call sites to
      // guarantee TriggerGenerateDocumentSection reads committed data.
      // Navigation/dialog callers keep the default (noTimeout=false) so
      // they still benefit from the 5-second safety timeout.
      if (!noTimeout) {
        setTimeout(() => {
          const idx = drainCallbacks.current.indexOf(resolve);
          if (idx !== -1) {
            drainCallbacks.current.splice(idx, 1);
            console.warn(
              '[PatientDataContext] flushPendingUpdates timed out after ' +
              `${FLUSH_TIMEOUT_MS}ms — proceeding with best-effort data`
            );
            resolve();
          }
        }, FLUSH_TIMEOUT_MS);
      }
    });
  }, [debouncedProcessQueue]);

  // Provide the context to the component tree, making the patient data and related functions accessible to any component that consumes this context.
  return (
    <PatientDataContext.Provider value={{ 
      patientDataModel, 
      loadedPatientId,
      fetchPatientData, 
      clearPatientData, 
      updatePatientFirestore,
      flushPendingUpdates,
      error
       }}>
      {children}
    </PatientDataContext.Provider>
  );
};